// Imports
import { faCalendarRange, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetEventsResponse } from '../../../../../../../shareables/types/api/main/website/homepage/events/get';
import formatDateRange from '../../../../../../../shareables/utils/format-date-range';
import BottomInfo from '../bottom-info';
import OccurrenceDetail from './detail';


// Styled components
const Occurrences = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	
	@media (max-width: 47.9375rem) {
		flex-direction: column;
		gap: 1.5rem;
	}
`;

const Occurrence = styled.div`
	flex: 1;
`;

const OccurrenceHeading = styled.strong`
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	
	font-size: 0.9375rem;
`;


// Define the accepted props
interface Props {
	storybookEvents?: GetEventsResponse;
}


// Function component
const UpcomingEvents: React.FC<Props> = ({ storybookEvents }) => {
	// Use state
	const [events, setEvents] = useState<GetEventsResponse | null>(storybookEvents ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const website = useAppSelector((state) => state.self.website);
	
	
	// Load events on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetEventsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/homepage/events`,
				completion: (json) => {
					setEvents(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Assign date and time formats
	const dateFormat = website?.formatting.dateFormat;
	const timeFormat = website?.formatting.timeFormat;
	
	
	// If loading, then display nothing
	if (events === null || !dateFormat || !timeFormat) {
		return null;
	}
	
	
	// Return JSX
	return (
		<BottomInfo>
			<h3>Upcoming events</h3>
			<Occurrences>
				{events?.map(({ occurrence, ...event }, i) => {
					// Build details
					const details: React.ComponentProps<typeof OccurrenceDetail>[] = [
						{
							text: formatDateRange({
								start: new Date(occurrence.start),
								end: occurrence.end ? new Date(occurrence.end) : undefined,
								dateFormat: dateFormat,
								timeFormat: timeFormat,
								allDay: event.allDay,
								shortenMonth: true,
							}),
							tooltip: 'When',
							icon: faCalendarRange,
						},
					];
					
					if (event.location) {
						details.push({
							text: event.location,
							tooltip: 'Location',
							icon: faLocationDot,
						});
					}
					
					return (
						<Occurrence key={i}>
							<OccurrenceHeading>{event.title}</OccurrenceHeading>
							{details.map((detail, i) => (
								<OccurrenceDetail key={i} {...detail} />
							))}
							<Link to={`${event.uri}/event/-${event.id}_${occurrence.id}`}>More info</Link>
						</Occurrence>
					);
				})}
			</Occurrences>
		</BottomInfo>
	);
};

export default UpcomingEvents;
